import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import QRCodeStyling from 'qr-code-styling';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { ExternalStatusType, InvoiceExternalEventDto, QrCodeDto } from 'types/invoices';
import { cn } from 'utils/cn';

type Props = {
    invoiceExternalEvents: InvoiceExternalEventDto[];
    externalStatusTypeCode?: string;
    externalPayload?: string | null;
    /** The new design's card: the three facts on its field grid, the number in mono. */
    isInCard?: boolean;
};

const qrCode = new QRCodeStyling({
    width: 150,
    height: 150,
    dotsOptions: {
        type: 'rounded',
    },
    imageOptions: {
        crossOrigin: 'anonymous',
        margin: 5,
    },
});

const KsefDetailsCard = ({
    invoiceExternalEvents,
    externalStatusTypeCode,
    externalPayload,
    isInCard = false,
}: Props) => {
    const qrCodeRef = useRef<HTMLDivElement>(null);

    const formattedPayload = useMemo(() => {
        if (!externalPayload) return null;
        try {
            return JSON.stringify(JSON.parse(externalPayload), null, 2);
        } catch {
            return externalPayload;
        }
    }, [externalPayload]);

    const latestInbound = [...invoiceExternalEvents]
        .filter((e) => e.directionTypeCode === 'INBOUND')
        .sort((a, b) => new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime())[0];

    const qrCodes = latestInbound?.qrCodes ?? [];
    const activeQrCode: QrCodeDto | undefined =
        qrCodes.find((qr) => qr.qrCodeType === 'KSEF') ?? qrCodes.find((qr) => qr.qrCodeType === 'OFFLINE');

    useEffect(() => {
        if (qrCodeRef.current) {
            qrCodeRef.current.innerHTML = '';
            qrCode.append(qrCodeRef.current);
        }
        qrCode.update({
            data: activeQrCode?.code || '',
        });
    }, [activeQrCode?.code]);

    const getKsefResponse = () => {
        if (externalStatusTypeCode === ExternalStatusType.SKIPPED) {
            return <FormattedMessage id="invoices.general-tab.ksef-details.skipped" />;
        }
        return latestInbound?.externalInvoiceState || '-';
    };

    const getStatusResponse = () => {
        if (!externalStatusTypeCode) return '-';
        if (externalStatusTypeCode === ExternalStatusType.SKIPPED) return '-';
        if (externalStatusTypeCode === ExternalStatusType.APPROVED) {
            return <FormattedMessage id="invoices.general-tab.ksef-details.status-ok" />;
        }
        if (externalStatusTypeCode === ExternalStatusType.PENDING) {
            return <FormattedMessage id="invoices.general-tab.ksef-details.status-pending" />;
        }
        if (externalStatusTypeCode === ExternalStatusType.REJECTED) {
            return latestInbound?.externalInvoiceStateReason || '-';
        }
        return '-';
    };

    return (
        <div className="flex flex-row items-start gap-8">
            {activeQrCode && (
                <div className="flex shrink-0 flex-col items-center gap-2">
                    <div ref={qrCodeRef} />
                    <span className="text-caption text-fg-secondary text-center whitespace-nowrap">
                        {activeQrCode.qrCodeType === 'KSEF' ? latestInbound?.externalInvoiceNumber : 'OFFLINE'}
                    </span>
                </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-4">
                {isInCard ? (
                    <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-2.5 @lg:grid-cols-3">
                        <CardField
                            labelId="invoices.general-tab.ksef-details.number"
                            className="font-mono font-normal break-all"
                        >
                            {latestInbound?.externalInvoiceNumber || '-'}
                        </CardField>
                        <CardField labelId="invoices.general-tab.ksef-details.response">{getKsefResponse()}</CardField>
                        <CardField labelId="invoices.general-tab.ksef-details.status-response">
                            {getStatusResponse()}
                        </CardField>
                    </dl>
                ) : (
                    <>
                        <KsefField labelId="invoices.general-tab.ksef-details.number">
                            {latestInbound?.externalInvoiceNumber || '-'}
                        </KsefField>
                        <KsefField labelId="invoices.general-tab.ksef-details.response">{getKsefResponse()}</KsefField>
                        <KsefField labelId="invoices.general-tab.ksef-details.status-response">
                            {getStatusResponse()}
                        </KsefField>
                    </>
                )}
                {formattedPayload && (
                    <details
                        className={cn(
                            'group border',
                            isInCard ? 'border-divider rounded-lg' : 'border-grey-200 rounded'
                        )}
                    >
                        <summary className="text-fg flex cursor-pointer list-none items-center justify-between px-3 py-2 text-sm select-none [&::-webkit-details-marker]:hidden">
                            <FormattedMessage id="invoices.general-tab.ksef-details.payload" />
                            <ExpandMoreIcon className="transition-transform group-open:rotate-180" />
                        </summary>
                        <div
                            className={cn(
                                'flex items-start gap-2 border-t px-3 py-2',
                                isInCard ? 'border-divider bg-surface-tile' : 'border-grey-200 bg-grey-50'
                            )}
                        >
                            <pre className="text-fg max-h-96 flex-1 overflow-auto font-mono text-[12.5px] leading-[1.7] break-words whitespace-pre-wrap">
                                {formattedPayload}
                            </pre>
                            <CopyToClipboardIcon text={formattedPayload} />
                        </div>
                    </details>
                )}
            </div>
        </div>
    );
};

/** A fact on the new design's field grid: its label over its value. */
const CardField = ({ labelId, className, children }: { labelId: string; className?: string; children: ReactNode }) => (
    <div className="flex min-w-0 flex-col gap-1">
        <dt className="text-muted-foreground text-xs leading-4">
            <FormattedMessage id={labelId} />
        </dt>
        <dd className={cn('m-0 py-2 text-sm font-medium', className)}>{children}</dd>
    </div>
);

/** A caption over its value. */
const KsefField = ({ labelId, children }: { labelId: string; children: ReactNode }) => (
    <div>
        <span className="text-caption text-fg-secondary">
            <FormattedMessage id={labelId} />
        </span>
        <p className="text-body1">{children}</p>
    </div>
);

export default KsefDetailsCard;
